/*
 * Copyright (c) 2024 Paul Ostrowski. All rights reserved.
 *
 * This software is the proprietary information of Paul Ostrowski.
 * Unauthorized copying, modification, or distribution of this code, via any medium, is strictly prohibited.
 * This code is proprietary and confidential.
 *
 * For licensing inquiries, contact Paul Ostrowski at postrowski@gmail.com.
*/

import Abilities from "model/abilities/Abilities";
import Armor from "model/stats/Armor";

class Utils {

    static sizes = ["Tiny", "Small", "Medium", "Large", "Huge", "Gargantuan"];
    static sizeIndexMap = new Map<string, number> (
        Utils.sizes.map((size, index) => [size, index])
    );

    static sortSizes = (sizeA: string, sizeB: string) => {
        const sizeAIndex: number = Utils.sizeIndexMap.get(sizeA) ?? -1;
        const sizeBIndex: number = Utils.sizeIndexMap.get(sizeB) ?? -1;
        return sizeAIndex > sizeBIndex ? 1 : sizeAIndex < sizeBIndex ? -1 : 0;
    };

    static alignments = ["Lawful Good", "Lawful Neutral", "Lawful Evil",
                         "Neutral Good", "Neutral", "Neutral Evil",
                         "Chaotic Good", "Chaotic Neutral", "Chaotic Evil",
                         "Any Alignment", "Unaligned"];

    static langs = ["Abyssal", "Aquan", "Auran", "Celestial", "Common", "Darakhul", "Deep Speech", "Draconic", "Druidic", "Dwarvish",
                    "Elvish", "Giant", "Gnomish", "Goblin", "Halfling", "Ignan", "Infernal", "Machine Speech", "Minotaur",
                    "Mushroomfolk", "Orc", "Primordial", "Sylvan", "Terran", "Thieves' cant", "Undercommon", "Void Speech"];

    static gear = [...Armor.getGearNames(), ...Abilities.getGearNames()];

    static damageTypes = ["acid", "bludgeoning", "cold", "fire", "force", "lightning", "necrotic",
                          "piercing", "poison", "psychic", "radiant", "slashing", "thunder",
                          "damage from nonmagical, non-silvered weapons",
                          "bludgeoning, piercing, and slashing from nonmagical attacks",
                          "bludgeoning, piercing, and slashing from nonmagical attacks not made with adamantine weapons",
                          "bludgeoning, piercing, and slashing from nonmagical attacks not made with silvered weapons"];
                          
    static conditions = ["blinded", "charmed", "deafened", "exhaustion", "frightened", "grappled",
                         "incapacitated", "invisible", "paralyzed", "petrified", "poisoned", "prone",
                         "restrained", "stunned", "unconscious"];
    // static damageTypes = ["Acid", "Bludgeoning", "Cold", "Fire", "Force", "Lightning", "Necrotic",
    //                       "Piercing", "Poison", "Psychic", "Radiant", "Slashing", "Thunder",
    //                       "Damage from nonmagical, non-silvered weapons",
    //                       "Bludgeoning, piercing, and slashing from nonmagical attacks",
    //                       "Bludgeoning, piercing, and slashing from nonmagical attacks not made with adamantine weapons",
    //                       "Bludgeoning, piercing, and slashing from nonmagical attacks not made with silvered weapons"];
                            
    // static conditions = ["Blinded", "Charmed", "Deafened", "Exhaustion", "Frightened", "Grappled",
    //                      "Incapacitated", "Invisible", "Paralyzed", "Petrified", "Poisoned", "Prone",
    //                      "Restrained", "Stunned", "Unconscious"];
  
    static parseCrString = (crString: string) => {
        const crStr = crString.split(/\s/)[0];
        const cr = (crStr === '1/2') ? 0.5 :
                 (crStr === '1/4') ? 0.25 :
                 (crStr === '1/8') ? 0.125 :
                                    ('' + parseInt(crStr) == crStr) ? parseInt(crStr) : NaN; // Handles integers and floating points directly
        if (isNaN(cr)) {
            return 1.0;
        }
        return cr;
    };

    static getProfBonusByCR = (cr: number | string) => {
        if (typeof cr === 'string') {
            cr = Utils.parseCrString(cr);
        }
        return 2 + Math.max(0, Math.floor((cr - 1) / 4))
    }
    static getXpByCR = (cr: number | string) => {
        if (typeof cr === 'string') {
            cr = Utils.parseCrString(cr);
        }
        switch (cr) {
            case 0: return 10;
            case .125: return 25;
            case .25: return 50;
            case .5: return 100;
            case 1: return 200;
            case 2: return 450;
            case 3: return 700;
            case 4: return 1100;
            case 5: return 1800;
            case 6: return 2300;
            case 7: return 2900;
            case 8: return 3900;
            case 9: return 5000;
            case 10: return 5900;
            case 11: return 7200;
            case 12: return 8400;
            case 13: return 10000;
            case 14: return 11500;
            case 15: return 13000;
            case 16: return 15000;
            case 17: return 18000;
            case 18: return 20000;
            case 19: return 22000;
            case 20: return 25000;
            case 21: return 33000;
            case 22: return 41000;
            case 23: return 50000;
            case 24: return 62000;
            case 25: return 75000;
            case 26: return 90000;
            case 27: return 105000;
            case 28: return 120000;
            case 29: return 135000;
            case 30: return 155000;
        }
        return 0;
    };

    static getHitDieBySize = (size: string) => {
        switch (size) {
            case "Tiny": return 4;
            case "Small": return 6;
            case "Medium": return 8;
            case "Large": return 10;
            case "Huge": return 12;
            case "Gargantuan": return 20;
        }
        return 8;
    };
}
export default Utils